import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { FileText } from 'lucide-react';
import { getSession } from '../../getSession';
import { getChannelTemplateListByUserId, getIsLeadRegisterForWhatsapp, associateDeissociateWrapper } from '../../utils/lead';
import Swal from 'sweetalert2';

const WhatsAppForm = forwardRef(({ lead } = {}, ref) => {
    const [selectedNumbers, setSelectedNumbers] = useState([]);
    const [selectedTemplate, setSelectedTemplate] = useState('');
    const [message, setMessage] = useState('');
    const [templates, setTemplates] = useState([]);
    const [placeholderValues, setPlaceholderValues] = useState({});
    const [isRegistered, setIsRegistered] = useState(true);
    const [isActionChecked, setIsActionChecked] = useState(false);
    const [isAssociating, setIsAssociating] = useState(false);
    const [registrationData, setRegistrationData] = useState(null);
    const [refreshTrigger, setRefreshTrigger] = useState(0);

    // helper to extract unique placeholders (full token like '[@CustomPlaceholder1@]')
    const extractPlaceholders = (tmpl) => {
        const view = tmpl?.json ?? null;
        const textSources = [];
        if (view && Array.isArray(view.components)) {
            view.components.forEach(c => {
                if (c.type && String(c.type).toUpperCase() === 'BODY' && c.text) textSources.push(c.text);
                if (c.type && String(c.type).toUpperCase() === 'HEADER' && (c.text || c.title)) textSources.push(c.text || c.title);
                if (c.type && String(c.type).toUpperCase() === 'FOOTER' && c.text) textSources.push(c.text);
            });
        }
        const joined = textSources.join('\n');
        const regex = /\[@(.*?)@\]/g;
        const found = [];
        let m;
        while ((m = regex.exec(joined)) !== null) {
            if (m[1]) found.push(m[0]);
        }
        return Array.from(new Set(found));
    };

    const getPlaceholderTokens = (text) => {
        if (!text) return [];
        const regex = /\[@(.*?)@\]/g;
        const found = [];
        let m;
        while ((m = regex.exec(String(text))) !== null) {
            if (m[0]) found.push(m[0]);
        }
        return found;
    };

    const buildTemplateComponents = (tmpl) => {
        const view = tmpl?.json ?? null;
        if (!view || !Array.isArray(view.components)) return [];

        const components = [];
        const byType = (type) => view.components.find(c => String(c?.type || '').toUpperCase() === type);

        const header = byType('HEADER');
        if (header && String(header?.format || '').toUpperCase() === 'TEXT') {
            const tokens = getPlaceholderTokens(header?.text || header?.title || '');
            if (tokens.length) {
                components.push({
                    type: 'header',
                    parameters: tokens.map((token) => ({
                        type: 'text',
                        text: String(placeholderValues[token] || '').trim()
                    }))
                });
            }
        }

        const body = byType('BODY');
        if (body) {
            const tokens = getPlaceholderTokens(body?.text || '');
            if (tokens.length) {
                components.push({
                    type: 'body',
                    parameters: tokens.map((token) => ({
                        type: 'text',
                        text: String(placeholderValues[token] || '').trim()
                    }))
                });
            }
        }

        const buttons = byType('BUTTONS');
        if (buttons && Array.isArray(buttons.buttons)) {
            buttons.buttons.forEach((btn, index) => {
                const btnText = btn?.text || btn?.title || '';
                const tokens = getPlaceholderTokens(btnText);
                if (tokens.length) {
                    components.push({
                        type: 'button',
                        sub_type: 'url',
                        index,
                        parameters: tokens.map((token) => ({
                            type: 'text',
                            text: String(placeholderValues[token] || '').trim()
                        }))
                    });
                }
            });
        }

        return components;
    };

    // derive mobile numbers from lead if provided, otherwise fall back to sample numbers
    const mobileNumbers = (() => {
        if (!lead) return ['8990005555', '9876543210', '8765432109'];
        const cand = [
            lead?.MobileNo, lead?.mobileNo, lead?.MobileNo1, lead?.mobileNo1,
            lead?.MobileNo2, lead?.mobileNo2
        ];
        const cleaned = cand
            .filter(Boolean)
            .map(s => String(s).trim())
            .filter(s => s.length > 0);
        // dedupe and keep order
        return Array.from(new Set(cleaned));
    })();

    const handleNumberToggle = (number) => {
        setSelectedNumbers(prev =>
            prev.includes(number)
                ? prev.filter(n => n !== number)
                : [...prev, number]
        );
    };

    const handleSelectAll = () => {
        if (selectedNumbers.length === mobileNumbers.length) {
            setSelectedNumbers([]);
        } else {
            setSelectedNumbers([...mobileNumbers]);
        }
    };

    useEffect(() => {
        let mounted = true;
        const loadTemplates = async () => {
            try {
                const { userId, TokenId } = getSession();

                // First, check if the lead is registered for WhatsApp
                const regPayload = {
                    Token: TokenId,
                    Message: '',
                    LoggedUserId: userId,
                    MAC_Address: '',
                    IP_Address: '',
                    Details: { LeadId: lead?.ID ?? lead?.LeadId ?? 0, EntityName: 'lead' },
                    BroadcastName: ''
                };

                const regResp = await getIsLeadRegisterForWhatsapp(regPayload);
                // regResp may be an object like { Id: 0, ChannelProfileId: '', Message: '...' }
                const registered = regResp && (Number(regResp?.Id ?? 0) > 0);

                if (mounted) {
                    setIsRegistered(!!registered);
                    setRegistrationData(regResp);
                }

                if (!registered) {
                    // not registered: clear templates and don't attempt to load them
                    if (mounted) {
                        setTemplates([]);
                        setSelectedTemplate('');
                    }
                    return;
                }

                const payload = {
                    Token: TokenId,
                    Message: '',
                    LoggedUserId: userId,
                    MAC_Address: '',
                    IP_Address: '',
                    Details: { usid: userId, Mode: 'approved' },
                    BroadcastName: ''
                };

                const resp = await getChannelTemplateListByUserId(payload);
                // resp expected to be array of ChannelTemplate objects
                const mapped = (Array.isArray(resp) ? resp : []).map(t => {
                    const rawJson = t?.kit19TemplateJSON ?? t?.templatejson ?? t?.TemplateJSON ?? t?.templateJson ?? null;
                    let parsed = null;
                    try {
                        if (rawJson) parsed = typeof rawJson === 'string' ? JSON.parse(rawJson) : rawJson;
                    } catch (e) {
                        console.warn('Failed to parse template json for template', t, e);
                    }
                    return {
                        id: String(t?.id ?? t?.Id ?? ''),
                        name: String(t?.templatename ?? t?.TemplateName ?? t?.name ?? ''),
                        type: String(t?.templatetype ?? t?.templatetype ?? t?.category ?? ''),
                        category: String(t?.category ?? t?.templatetype ?? parsed?.category ?? '').toLowerCase(),
                        namespace: String(t?.namespace ?? t?.Namespace ?? parsed?.namespace ?? ''),
                        language: String(t?.language ?? t?.Language ?? parsed?.language?.code ?? parsed?.language ?? ''),
                        raw: rawJson,
                        json: parsed
                    };
                });
                if (mounted) {
                    setTemplates(mapped);
                    if (!selectedTemplate && mapped.length) setSelectedTemplate(mapped[0].id);
                }
            } catch (err) {
                console.warn('load channel templates error', err);
            }
        };
        loadTemplates();
        return () => { mounted = false; };
    }, [lead?.ID, refreshTrigger]);

    const handleAssociate = async () => {
        const result = await Swal.fire({
            title: "",
            text: "This action will dissociate the WhatsApp no. from existing lead/enquiry and associate it with this entity. This action WILL NOT move the previous conversations go the new entity. They will be available in that entity itself?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Continue",
            cancelButtonText: "Cancel",
            customClass: {
                title: 'text-lg font-bold',
                content: 'text-sm'
            }
        });

        if (result.isConfirmed) {
            setIsAssociating(true);
            try {
                const { userId, TokenId } = getSession();
                const details = {
                    oldEntityName: 'lead',//registrationData?.EntityName || '',
                    oldEntityId: parseInt(registrationData?.EntityId) || 0,
                    EntityId: lead?.ID || lead?.LeadId || 0,
                    EntityName: 'lead',
                    ChannelName: 'whatsapp',
                    ChannelProfileId: registrationData?.ChannelProfileId || (mobileNumbers[0] ? mobileNumbers[0].replace(/\D/g, '') : ''),
                    profileJson: JSON.stringify({}),
                    UserId: userId
                };
                const payload = {
                    Token: TokenId,
                    Message: '',
                    LoggedUserId: userId,
                    MAC_Address: '',
                    IP_Address: '',
                    Details: details,
                    BroadcastName: ''
                };


                const response = await associateDeissociateWrapper(payload);
                const data = response?.Details || response;

                if (data?.status || data?.Status || response?.Status === 1) {
                    Swal.fire('Info', `${data?.message || data?.Message || 'Associated successfully'}`, 'info');
                    setRefreshTrigger(prev => prev + 1);
                    setIsActionChecked(false);
                } else {
                    Swal.fire('Error', `${data?.message || data?.Message || 'Association failed'}`, 'error');
                }
            } catch (err) {
                console.warn('handleAssociate error', err);
                Swal.fire('Error', 'An error occurred during association', 'error');
            } finally {
                setIsAssociating(false);
            }
        }
    };

    // initialize placeholderValues whenever the selected template changes
    useEffect(() => {
        const tmpl = templates.find(t => t.id === selectedTemplate) || null;
        const uniquePlaceholders = extractPlaceholders(tmpl);
        setPlaceholderValues(prev => {
            const map = {};
            uniquePlaceholders.forEach(p => { map[p] = prev[p] || ''; });
            return map;
        });
    }, [selectedTemplate, templates]);

    useImperativeHandle(ref, () => ({
        getFormData: () => {
            const selectedTemplateData = templates.find(t => t.id === selectedTemplate) || null;
            return {
                LeadId: lead?.ID || lead?.LeadId || lead?.Id || lead?.id || 0,
                EntityType: 'lead',
                TemplateId: selectedTemplate || '',
                TemplateName: selectedTemplateData?.name || '',
                TemplateNamespace: selectedTemplateData?.namespace || '',
                TemplateLanguage: selectedTemplateData?.language || '',
                TemplateCategory: selectedTemplateData?.category || '',
                TemplateComponents: buildTemplateComponents(selectedTemplateData),
                Message: message || '',
                selectedNumbers: Array.isArray(selectedNumbers) ? selectedNumbers : [],
                PlaceholderValues: placeholderValues || {}
            };
        }
    }), [lead?.ID, selectedTemplate, message, selectedNumbers, placeholderValues, templates]);

    return (
        <div className="w-full max-h-[600px] overflow-y-auto px-1">
            {!isRegistered ? (
                <div className="py-8 px-4 text-center">
                    <div className="mb-6 flex flex-col items-center">
                        <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mb-4">
                            <FileText className="w-8 h-8 text-green-600" />
                        </div>
                        <h3 className="text-lg font-bold text-gray-800 mb-2">WhatsApp Channel Not Associated</h3>
                        <p className="text-sm text-gray-500 max-w-sm mx-auto mb-6">
                            This lead's number is not currently associated with your WhatsApp channel.
                            Would you like to associate it now?
                        </p>

                        <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg border border-gray-100 mb-6">
                            <input
                                type="checkbox"
                                id="action-checkbox"
                                checked={isActionChecked}
                                onChange={(e) => setIsActionChecked(e.target.checked)}
                                className="w-5 h-5 rounded border-gray-300 text-green-600 focus:ring-2 focus:ring-green-500 cursor-pointer"
                            />
                            <label htmlFor="action-checkbox" className="text-sm font-medium text-gray-700 cursor-pointer">
                                I want to associate this number
                            </label>
                        </div>

                        {isActionChecked && (
                            <div className="flex justify-center">
                                <button
                                    onClick={handleAssociate}
                                    disabled={isAssociating}
                                    className="px-8 py-2.5 bg-green-600 hover:bg-green-700 text-white font-bold rounded-lg shadow-md transition-all active:scale-95 disabled:opacity-50 disabled:pointer-events-none"
                                >
                                    {isAssociating ? 'Associating...' : (registrationData?.EntityId > 0 ? 'Disassociate then associate' : 'Associate')}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            ) : (
                <>
                    {/* Choose Mobile Numbers */}
                    <div className="mb-6">
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="text-sm font-semibold text-gray-700">Choose Mobile Numbers</h3>
                            <button
                                onClick={handleSelectAll}
                                className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                            >
                                {selectedNumbers.length === mobileNumbers.length ? 'Deselect All' : 'Select All'}
                            </button>
                        </div>
                        <div className="space-y-2">
                            {mobileNumbers.map((number, index) => (
                                <label
                                    key={index}
                                    className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded cursor-pointer"
                                >
                                    <input
                                        type="checkbox"
                                        checked={selectedNumbers.includes(number)}
                                        onChange={() => handleNumberToggle(number)}
                                        className="w-4 h-4 rounded border-gray-300 text-green-600 focus:ring-2 focus:ring-green-500"
                                    />
                                    <span className="text-sm text-gray-700">{number}</span>
                                </label>
                            ))}
                        </div>
                        {selectedNumbers.length > 0 && (
                            <p className="text-xs text-gray-500 mt-2">
                                {selectedNumbers.length} number{selectedNumbers.length > 1 ? 's' : ''} selected
                            </p>
                        )}
                    </div>

                    {/* Template Selection */}
                    <div className="mb-4">
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Select Template
                        </label>
                        <select
                            value={selectedTemplate}
                            onChange={(e) => setSelectedTemplate(e.target.value)}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        >
                            <option value="">--Select Template--</option>
                            {templates.map((template) => (
                                <option key={template.id} value={template.id}>
                                    {template.name} ({template.type})
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Template Preview */}
                    {selectedTemplate && (
                        <div className="mb-4">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Template Preview
                            </label>
                            <div className="p-3 bg-gray-50 border border-gray-200 rounded-md">
                                {(() => {
                                    const tmpl = templates.find(t => t.id === selectedTemplate) || null;
                                    const view = tmpl?.json ?? null;
                                    if (!view) {
                                        return (
                                            <p className="text-sm text-gray-700">No preview available for this template.</p>
                                        );
                                    }

                                    const comps = Array.isArray(view.components) ? view.components : [];
                                    const header = comps.find(c => String(c.type).toUpperCase() === 'HEADER');
                                    const body = comps.find(c => String(c.type).toUpperCase() === 'BODY');
                                    const footer = comps.find(c => String(c.type).toUpperCase() === 'FOOTER');
                                    const buttonsComp = comps.find(c => String(c.type).toUpperCase() === 'BUTTONS');

                                    return (
                                        <div>
                                            {header && (
                                                <div className="mb-2">
                                                    {header.format && String(header.format).toUpperCase() === 'TEXT' && (
                                                        <p className="font-semibold text-gray-800">{header.text}</p>
                                                    )}
                                                    {header.format && String(header.format).toUpperCase() === 'IMAGE' && (
                                                        <div className="mb-2"><img src={header.text || header.url || ''} alt="header" className="max-w-full h-auto rounded" /></div>
                                                    )}
                                                </div>
                                            )}

                                            {body && (
                                                <div className="mb-2">
                                                    <p className="text-sm text-gray-700 whitespace-pre-line">{body.text}</p>
                                                </div>
                                            )}

                                            {footer && (
                                                <div className="mt-3 border-t pt-2">
                                                    <p className="text-xs text-gray-600">{footer.text}</p>
                                                </div>
                                            )}

                                            {buttonsComp && buttonsComp.buttons && Array.isArray(buttonsComp.buttons) && (
                                                <div className="mt-3 flex flex-wrap gap-2">
                                                    {buttonsComp.buttons.map((btn, i) => (
                                                        <button key={i} type="button" className="px-3 py-1 text-xs bg-white border rounded text-gray-700">
                                                            {btn.text || btn.title || btn.name}
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })()}
                            </div>
                        </div>
                    )}

                    {/* Message Variables: base variables + extracted custom placeholders from the template */}
                    <div className="mb-6">
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Available Variables
                        </label>
                        <div className="flex flex-wrap gap-2">
                            {(() => {
                                // compute placeholders from selected template (e.g. [@CustomPlaceholder1@])
                                const tmpl = templates.find(t => t.id === selectedTemplate) || null;
                                const uniquePlaceholders = extractPlaceholders(tmpl);

                                // render the placeholders as variable buttons
                                const varButtons = uniquePlaceholders.map((variable) => (
                                    <button
                                        key={variable}
                                        onClick={() => setMessage(prev => (prev || '') + variable)}
                                        className="px-3 py-1.5 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded border border-gray-300 transition"
                                        type="button"
                                    >
                                        {variable}
                                    </button>
                                ));

                                if (uniquePlaceholders.length === 0) {
                                    return <p className="text-sm text-gray-600">No custom placeholders found in this template.</p>;
                                }

                                return varButtons;
                            })()}
                        </div>
                    </div>

                    {/* Placeholder Example Inputs: show a simple input for each placeholder so user can supply example values */}
                    {(() => {
                        const tmpl = templates.find(t => t.id === selectedTemplate) || null;
                        const uniquePlaceholders = extractPlaceholders(tmpl);

                        if (uniquePlaceholders.length === 0) return null;

                        return (
                            <div className="mb-6">
                                <label className="block text-sm font-semibold text-gray-700 mb-2">Example values</label>
                                <div className="space-y-3">
                                    {uniquePlaceholders.map((ph) => (
                                        <div key={ph} className="flex flex-col">
                                            <label className="text-xs font-medium text-gray-700 mb-1">{ph}</label>
                                            <input
                                                type="text"
                                                value={placeholderValues[ph] ?? ''}
                                                onChange={(e) => setPlaceholderValues(prev => ({ ...prev, [ph]: e.target.value }))}
                                                className="px-3 py-2 text-sm border border-gray-300 rounded-md"
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    })()}
                </>
            )}
        </div>
    );
});

export default WhatsAppForm;
